import React, { useEffect, useState } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js/lib/core';
import pie from 'plotly.js/lib/pie';
import { CircularProgress, Box } from '@mui/material';

Plotly.register([pie]);
const Plot = createPlotlyComponent(Plotly);

const LatePaymentsPieChart = ({
  data,
  loading,
  error,
  chartTitle = 'Late vs On-Time Payments: ',
  onLoaded,
  hoveredPeriod,
}) => {
  const [chartData, setChartData] = useState(null);
  const [usedPeriod, setUsedPeriod] = useState(null);

  const formatPeriod = (period) => {
    if (!period) return 'Latest Period';
    const [year, month] = period.split('-');
    const date = new Date(Number(year), Number(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  useEffect(() => {
    if (!data || data.length === 0) return;

    let periodToUse = hoveredPeriod;

    if (!periodToUse) {
      const latest = data
        .filter((x) => x.paymentDatePeriodEpoch)
        .sort((a, b) => b.paymentDatePeriodEpoch - a.paymentDatePeriodEpoch)[0];
      periodToUse = latest?.paymentDatePeriod;
    }

    setUsedPeriod(periodToUse);

    const filtered = periodToUse ? data.filter((x) => x.paymentDatePeriod === periodToUse) : data;

    const colors = ['#0E2756', '#B0C3E5'];

    const groupedData = filtered.reduce(
      (acc, { lateOnTime }) => {
        const label = lateOnTime?.toLowerCase();
        if (label === 'late payment') acc['Late Payment'] += 1;
        else if (label === 'on time') acc['On Time'] += 1;
        return acc;
      },
      { 'Late Payment': 0, 'On Time': 0 }
    );

    const labels = Object.keys(groupedData);
    const values = Object.values(groupedData);

    const pieTrace = {
      labels,
      values,
      type: 'pie',
      name: chartTitle,
      marker: { colors },
      hoverinfo: 'label+percent+value',
      textinfo: 'label+percent',
    };

    setChartData([pieTrace]);
    onLoaded?.();
  }, [hoveredPeriod, data, onLoaded, chartTitle]);

  const layout = {
    title: {
      text: usedPeriod ? `${chartTitle} ${formatPeriod(usedPeriod)}` : chartTitle,
      font: { size: 18 },
    },
    margin: { l: 10, r: 10, t: 80, b: 10 },
    showlegend: true,
    autosize: true,
    legend: { orientation: 'v', x: 1.05, y: 1, xanchor: 'center' },
  };

  const config = {
    responsive: true,
    displaylogo: false,
    displayModeBar: 'hover',
    modeBarButtonsToRemove: ['zoom2d', 'pan2d', 'select2d', 'lasso2d'],
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <p>Error: {error}</p>;
  if (!chartData) return <p>No data available.</p>;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Plot data={chartData} layout={layout} config={config} style={{ width: '100%', height: '400px' }} />
    </div>
  );
};

export default LatePaymentsPieChart;
